import { useEffect, useRef } from "react"

import { faIcon } from "@utils/faIcon"

import { Skeleton } from "@particles/index"

import EmptyState from "@molecules/EmptyState/EmptyState"
import toast from "@molecules/Toast/Toast"

import { Settings } from "@templates/index"

import styles from "./SettingsEcommerce.module.scss"
import { useEcommerceConnectionsQuery, useEcommercePlatformsQuery } from "./SettingsEcommerce.store"
import { OAUTH_RESULT_PARAM_NAMES, extractOAuthResult } from "./SettingsEcommerce.utils"
import EcommercePlatformCard from "./components/EcommercePlatformCard/EcommercePlatformCard"

function SettingsEcommerceContent() {
    const platformsQuery = useEcommercePlatformsQuery()
    const connectionsQuery = useEcommerceConnectionsQuery()

    // Surface the oauth callback's outcome once, then strip its params from the URL.
    const hasHandledOAuthResultRef = useRef(false)
    useEffect(() => {
        if (hasHandledOAuthResultRef.current) {
            return
        }
        hasHandledOAuthResultRef.current = true

        const searchParams = new URLSearchParams(window.location.search)
        const oauthResult = extractOAuthResult(searchParams)
        if (!oauthResult) {
            return
        }

        if (oauthResult.isSuccess) {
            toast({ type: "success", title: "Store connected" })
        } else {
            toast({
                type: "error",
                variant: "detailed",
                title: "Couldn't connect the store",
                supportText: oauthResult.errorMessage,
            })
        }

        OAUTH_RESULT_PARAM_NAMES.forEach((paramName) => searchParams.delete(paramName))
        const remainingQuery = searchParams.toString()
        window.history.replaceState(null, "", window.location.pathname + (remainingQuery ? `?${remainingQuery}` : ""))
    }, [])

    const renderPlatformCards = () => {
        if (platformsQuery.isLoading || connectionsQuery.isLoading) {
            return (
                <div className={styles.skeleton}>
                    <Skeleton height={88} />
                </div>
            )
        }

        if (platformsQuery.isError || connectionsQuery.isError) {
            return (
                <div className={styles.emptyStateContainer}>
                    <EmptyState
                        variant="error"
                        icon={faIcon({ name: "circle-xmark", style: "solid" })}
                        title="Hmm, something went wrong..."
                        supportText="An error occurred while fetching e-commerce platforms."
                        primaryCTA={{
                            children: "Try again",
                            onClick: () => {
                                void platformsQuery.refetch()
                                void connectionsQuery.refetch()
                            },
                        }}
                    />
                </div>
            )
        }

        const connections = connectionsQuery.data ?? []

        return (platformsQuery.data ?? []).map((platform) => (
            <EcommercePlatformCard
                key={platform.platform}
                platform={platform}
                connections={connections.filter((connection) => connection.platform === platform.platform)}
            />
        ))
    }

    return (
        <Settings.PageContainer>
            <Settings.PageHeader
                variant="page-heading"
                title="E-Commerce"
                subtitle="Connect your suppliers' e-commerce stores to Roopairs."
            />

            <Settings.Divider />

            <Settings.Section>{renderPlatformCards()}</Settings.Section>
        </Settings.PageContainer>
    )
}

export default function SettingsEcommerce() {
    return (
        <Settings.Wrapper title="E-Commerce">
            <SettingsEcommerceContent />
        </Settings.Wrapper>
    )
}
